<script setup lang="ts">
  import { computed, type HTMLAttributes } from 'vue'
  import { testIds } from '@gat-ship/types'
  import { useAccessRoleDetailProvider } from './providers/access-role-detail-context'
  import { useAccessRolesCollectionProvider } from './providers/access-roles-collection-context'
  import AccessRoleSelect from './inputs/AccessRoleSelect.vue'
  import { useUser } from '@gat-ship/user'
  import { IconMoreHorizontal, IconEdit, IconTrash } from '@gat-ship/icons'
  import {
    cn,
    ButtonGroup,
    ButtonGroupSeparator,
    Button,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem
  } from '@gat-ship/vue-components'

  // The access-role switcher: the role Select + the change-name / delete actions
  // dropdown. Shared by the routed AccessRolePermissionsSidebar and the
  // AdministrationModal AccessRoleDetailModal. Reads the current role + its
  // action modals from the detail context and the add action from the collection
  // context; the host decides what switching the role does via `select`.
  const props = defineProps<{
    accessRoleId: string
    class?: HTMLAttributes['class']
  }>()

  const emit = defineEmits<{
    (e: 'select', roleId: string): void
  }>()

  const user = useUser()

  const { dataSource, modals } = useAccessRoleDetailProvider()
  const accessRolesProvider = useAccessRolesCollectionProvider()

  const accessRole = computed(() => dataSource.accessRole.value)
</script>

<template>
  <ButtonGroup :class="cn('w-full', props.class)">
    <AccessRoleSelect
      :model-value="accessRoleId"
      class="grow"
      required
      input-class="h-10 rounded-r-none border-r-0"
      :pre-selected-access-role="accessRole"
      @update:model-value="(value) => value && emit('select', value)"
      @click:add-access-role="accessRolesProvider.modals.showAddAccessRole({})"
    />
    <ButtonGroupSeparator />
    <DropdownMenu v-if="accessRole">
      <DropdownMenuTrigger as-child>
        <Button
          variant="outline"
          size="icon"
          class="size-10 rounded-l-none shadow-none"
          :data-testid="
            testIds.accessRole.actions.actionDropdown(accessRole.id)
          "
        >
          <IconMoreHorizontal class="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          v-if="user.isTenantAdmin.value"
          :data-testid="testIds.accessRole.actions.changeName"
          @click="modals.showChangeAccessRoleName({ accessRole })"
        >
          <IconEdit class="size-4" />
          Change Name
        </DropdownMenuItem>
        <DropdownMenuItem
          v-if="user.isTenantAdmin.value"
          :data-testid="testIds.accessRole.actions.delete"
          @click="modals.showDeleteAccessRole({ accessRole })"
        >
          <IconTrash class="size-4" />
          Delete Role
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </ButtonGroup>
</template>
